import { type ReactElement, type ReactNode, useMemo } from "react";

import { useCurrencyQuery } from "@/api/admin/useCurrencyQuery";
import { CurrencySymbol } from "@/components/molecules/CurrencySymbol";
import { formatNumber, formatValue } from "@/utils/formatNumber";
import { formatPrice } from "@/utils/formatPrice";
import { renderMoneyValue } from "@/utils/renderMoneyValue";

import { useAdminCurrency } from "./useAdminCurrency";

/** SAR and JOD are written with 2 and 3 decimals; used until the options load. */
const FALLBACK_DECIMALS = 2;

export interface AdminMoney {
  /** ISO code of the admin's selected currency, e.g. "JOD". */
  code: string;
  decimals: number;
  /** The currency's symbol, sized with `classes` (SAR is drawn as an image). */
  symbol: (classes?: string, color?: string) => ReactNode;
  /** `1,234.500`-style text, without a symbol. A whole number has no decimals. */
  format: (value: number) => string;
  /** Same as `formatValue`, with the currency's decimals for money. */
  formatValue: (
    value: number | null | undefined,
    isMonetary?: boolean
  ) => string;
  /** A table cell: the symbol, then the amount written with the currency's decimals. */
  render: (
    value: string | undefined,
    prefix?: string,
    isFormatting?: boolean
  ) => ReactElement;
  /** An amount written with the currency's decimals: `2.75` is `2.750` in JOD. */
  pad: (value: number | string | null | undefined) => string;
  /** `12.5` in a label, e.g. "K JOD" for the overview charts. */
  thousandsLabel: string;
}

/**
 * What a screen needs to show money in a currency: its symbol, its decimals and
 * its code for labels. It is the currency the admin picked in the top bar,
 * unless the screen shows one thing with a currency of its own (an order, a
 * top-up) and passes that code.
 */
export const useAdminMoney = (currencyCode?: string | null): AdminMoney => {
  const filterCode = useAdminCurrency();
  const code = currencyCode || filterCode;
  const currency = useCurrencyQuery(code);
  const decimals = currency?.decimals ?? FALLBACK_DECIMALS;

  const symbolText = currency?.symbol ?? code;

  return useMemo(
    () => ({
      code,
      decimals,
      symbol: (classes?: string, color?: string) => (
        <CurrencySymbol
          code={code}
          symbol={symbolText}
          classes={classes}
          color={color}
        />
      ),
      format: (value: number) =>
        formatNumber(value, undefined, decimals) as string,
      formatValue: (value: number | null | undefined, isMonetary = false) =>
        formatValue(value, isMonetary, decimals),
      render: (value: string | undefined, prefix = "", isFormatting = false) =>
        renderMoneyValue(value, prefix, isFormatting, {
          code,
          symbol: symbolText,
          decimals,
        }),
      pad: (value: number | string | null | undefined) =>
        formatPrice(value, decimals) || String(value ?? ""),
      thousandsLabel: `K ${code}`,
    }),
    [code, symbolText, decimals]
  );
};
